import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { platformAPI, dspAPI } from '../../lib/dspAPI';
import { errMsg } from '../../lib/atoAPI';

const ask = (fields) => {
  const out = {};
  for (const [k, label] of fields) { const v = window.prompt(label); if (v === null) return null; out[k] = v; }
  return out;
};

export const ProviderCard = () => {
  const [p, setP] = useState(null);
  useEffect(() => { platformAPI.provider().then(setP).catch((e) => toast.error(errMsg(e))); }, []);
  const save = async () => { try { setP(await platformAPI.saveProvider(p)); toast.success('Saved'); } catch (e) { toast.error(errMsg(e)); } };
  if (!p) return null;
  return (
    <Card data-testid="platform-provider"><CardHeader><CardTitle className="text-base">Software provider</CardTitle></CardHeader>
      <CardContent className="grid gap-2 md:grid-cols-3">
        <Input value={p.legal_name} onChange={(e) => setP({ ...p, legal_name: e.target.value })} data-testid="platform-provider-legal-name" />
        <Input value={p.product_name} onChange={(e) => setP({ ...p, product_name: e.target.value })} data-testid="platform-provider-product" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!p.legal_name_confirmed} onCheckedChange={(v) => setP({ ...p, legal_name_confirmed: v })} data-testid="platform-provider-confirmed" />Legal name confirmed</label>
        <Button onClick={save} className="w-fit" data-testid="platform-provider-save">Save</Button></CardContent></Card>
  );
};

export const SubscriptionsCard = ({ onPickOrg }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => platformAPI.subs().then(setD).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (s, status) => {
    let body = { status };
    if (status === 'Verified') {
      const ev = ask([['evidence_reference', 'Evidence reference / ticket number'], ['verification_method', 'Verification method'], ['verified_on', 'Verification date (YYYY-MM-DD)'], ['verifier', 'Verifier name']]);
      if (!ev) return; body = { ...body, ...ev };
    } else { const reason = window.prompt('Reason'); if (reason === null) return; body.reason = reason; }
    try { await platformAPI.setStatus(s.organization_id, body); toast.success(`Status: ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  return (
    <Card data-testid="platform-subscriptions"><CardHeader><CardTitle className="text-base">Practice ATO subscriptions</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">{d.items.length === 0 && <p className="text-muted-foreground">No practice has started ATO connection.</p>}
        {d.items.map((s) => (
          <div key={s.organization_id} className="flex flex-wrap items-center gap-2 border-b py-2" data-testid={`platform-sub-${s.organization_id}`}>
            <span className="flex-1 font-medium">{s.organization_name} <code className="ml-2 text-xs">{s.ssid || '—'}</code></span>
            <Badge variant="outline">{s.status}</Badge>
            {['Verified', 'Suspended', 'Revoked', 'Error'].map((st) => <Button key={st} size="sm" variant="outline" onClick={() => setStatus(s, st)} data-testid={`platform-sub-set-${st}`}>{st}</Button>)}
            <Button size="sm" variant="ghost" onClick={() => onPickOrg(s.organization_id)} data-testid="platform-sub-gates">Gates</Button>
          </div>))}</CardContent></Card>
  );
};

export const GatesCard = ({ org, orgs, onOrg }) => {
  const [rows, setRows] = useState([]);
  const [svc, setSvc] = useState(null);
  const load = useCallback(() => (org ? dspAPI(true).gates(org).then((d) => { setRows(d.items); setSvc((s) => s || d.items[0]?.service_id); }).catch((e) => toast.error(errMsg(e))) : null), [org]);
  useEffect(() => { load(); }, [load]);
  const g = rows.find((r) => r.service_id === svc);
  const setItem = async (it, confirm) => {
    let body = { status: confirm ? 'confirmed' : 'unconfirmed' };
    if (confirm) {
      const ev = ask([['evidence_reference', 'Evidence reference'], ['verifier', 'Verifier'], ['date', 'Date (YYYY-MM-DD)'], ...(it.key === 'release_approval' ? [['approver', 'Named release approver']] : [])]);
      if (!ev) return; body = { ...body, ...ev };
    }
    try { await platformAPI.gateItem(org, svc, it.key, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="platform-gates"><CardHeader><CardTitle className="text-base">Production activation gate</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-2">
          <Select value={org || ''} onValueChange={onOrg}><SelectTrigger className="w-64" data-testid="platform-gate-org"><SelectValue placeholder="Practice" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          {rows.length > 0 && <Select value={svc || ''} onValueChange={setSvc}><SelectTrigger className="w-64" data-testid="platform-gate-service"><SelectValue /></SelectTrigger>
            <SelectContent>{rows.map((r) => <SelectItem key={r.service_id} value={r.service_id}>{r.service_id}</SelectItem>)}</SelectContent></Select>}
        </div>
        {g && <>
          <p data-testid="platform-gate-summary">EVTE: {g.evte_unmet.length ? 'blocked' : 'allowed'} · Production: {g.production_unmet.length ? `${g.production_unmet.length} unmet` : 'all confirmed'}</p>
          {g.checklist.map((it) => (
            <div key={it.key} className="flex flex-wrap items-center gap-2 border-b py-1.5" data-testid={`platform-gate-item-${it.key}`}>
              <span className="flex-1">{it.label}</span>
              {it.status === 'confirmed' ? <span className="text-xs text-emerald-700">confirmed · {it.evidence_reference} · {it.verifier} · {it.date}</span> : <span className="text-xs text-muted-foreground">unconfirmed</span>}
              <Button size="sm" variant="outline" onClick={() => setItem(it, it.status !== 'confirmed')} data-testid={`platform-gate-toggle-${it.key}`}>{it.status === 'confirmed' ? 'Unconfirm' : 'Confirm with evidence'}</Button>
            </div>))}
        </>}
      </CardContent></Card>
  );
};
